import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  ViewChild,
  AfterContentInit,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Roles } from 'src/app/shared/enums/Roles.enum';
import { LaftService } from 'src/app/shared/services/laft.service';
import { LaftStepperComponent } from 'src/app/shared/UI/organisms/laft-stepper/laft-stepper/laft-stepper.component';

@Component({
  selector: 'app-laft-legal-modal',
  templateUrl: './laft-legal-modal.component.html',
  styleUrls: ['./laft-legal-modal.component.scss'],
})
export class LaftLegalModalComponent implements OnInit, OnDestroy, AfterContentInit, AfterViewInit {
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() saveEvent = new EventEmitter<any>();
  @ViewChild(LaftStepperComponent) stepper!: LaftStepperComponent;

  readonly Roles = Roles;
  public currentStep = 1;
  public totalSteps = 4;
  private unsubscribe$ = new Subject<void>();
  private formData: any = {
    step1Data: {},
    step2Data: {},
    step3Data: {},
    step4Data: {},
  };
  private formInProgress = false;
  public initFormData: any = {};
  public legalRepresentativesList: { value: string; text: string }[] = [];
  public firstLegalRepresentativeName: string = '';
  public isPendingPepFlow: boolean = false;
  public isSavingStep = false;
  public airtableRepresentativesList: {
    value: string;
    text: string;
    documentNumber: string;
    names: string;
    lastNames: string;
  }[] = [];

  constructor(
    public dialogRef: MatDialogRef<LaftLegalModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private route: ActivatedRoute,
    private laftService: LaftService
  ) {
    if (this.data?.laftData) {
      this.formInProgress = true;
      this.initFormData = this.data?.laftData;
      if (this.initFormData) {
        if (!Array.isArray(this.initFormData.airtableRepresentativeData)) {
          this.initFormData.airtableRepresentativeData = [];
        }
        if (!Array.isArray(this.initFormData.missingRepresentatives)) {
          this.initFormData.missingRepresentatives = [];
        }
      }
      this.loadFormDataFromDeclaration(this.data.laftData);
      this.updateLegalRepresentativesList();
      this.updateFirstLegalRepresentativeName();
      this.checkPendingPepFlow();
    }
  }

  private loadFormDataFromDeclaration(laft: any): void {
    if (!laft || typeof laft !== 'object') return;
    this.formData = {
      ...this.formData,
      step1Data: { ...laft },
      step2Data: {
        ...this.formData.step2Data,
        shareHolders: Array.isArray(laft.shareHolders) ? laft.shareHolders : this.formData.step2Data?.shareHolders,
      },
    };
  }

  ngOnInit(): void {
    this.laftService.isSavingStep$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(isSavingStep => (this.isSavingStep = isSavingStep));

    this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      const laftStep = parseInt(params['laft_step']);
      if (laftStep && laftStep >= 1 && laftStep <= this.totalSteps) {
        this.updateCurrentStep(laftStep);
        this.formInProgress = true;
      }
    });

    if (localStorage.getItem('cleanUrlOnReload') === 'true' && this.isPageReload()) {
      setTimeout(() => {
        localStorage.removeItem('cleanUrlOnReload');
      }, 500);
    }

    history.pushState(null, '', window.location.href);
  }

  private isPageReload(): boolean {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (entries && entries.length > 0) {
        return (entries[0] as any).type === 'reload';
      }
      if (performance.navigation) {
        return performance.navigation.type === performance.navigation.TYPE_RELOAD;
      }
    } catch {
      return false;
    }
    return false;
  }

  ngAfterContentInit(): void {
    const initialStep = this.data?.initialStep;
    const laftStepRaw = this.data?.laftData?.step;

    if (typeof initialStep === 'number' && initialStep >= 1 && initialStep <= this.totalSteps) {
      const step = initialStep;
      this.updateCurrentStep(step);
      this.updateUrlStep(step);
      this.formInProgress = true;
    } else if (
      typeof laftStepRaw === 'number' ||
      (typeof laftStepRaw === 'string' && laftStepRaw.trim() !== '')
    ) {
      const step = parseInt(laftStepRaw as any, 10);
      if (!isNaN(step) && step >= 1 && step <= this.totalSteps) {
        this.updateCurrentStep(step);
        this.updateUrlStep(step);
        this.formInProgress = true;
      } else {
        this.updateCurrentStep(1);
        this.updateUrlStep(1);
      }
    } else {
      this.updateCurrentStep(1);
      this.updateUrlStep(1);
    }

    this.updateLegalRepresentativesList();
    this.updateFirstLegalRepresentativeName();
  }

  ngAfterViewInit(): void {
    // Ensure the stepper reflects the current step once the view is initialized.
    setTimeout(() => {
      this.updateStepperStep(this.currentStep);
    });
  }

  ngOnDestroy(): void {
    this.removeUrlStep();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.formInProgress) {
      event.preventDefault();
      event.returnValue = '';
      localStorage.setItem('cleanUrlOnReload', 'true');
    }
  }

  onPreviousStep(): void {
    if (this.stepper) {
      this.stepper.previous();
    }

    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateUrlStep(this.currentStep);
    }
  }

  onStepperSubmit(formData?: any): void {
    const payload = this.buildPayload(formData, true, false);
    const isPendingPepFlowSubmit =
      this.data?.initialStep === 3 || !!this.data?.laftData?.pendingAdditionalPepInfo;
    this.formSubmitted.emit({ payload, isPendingPepFlowSubmit });
    this.close();
  }

  onFormNextStep(nextStepNumber: number, stepData?: any): void {
    this.currentStep = nextStepNumber;
    if (this.formData) {
      this.formData.step = nextStepNumber.toString();
    }
    if (stepData) {
      const currentStep = nextStepNumber - 1;
      const stepKey = `step${currentStep}Data` as 'step1Data' | 'step2Data' | 'step3Data' | 'step4Data';
      this.formData = {
        ...this.formData,
        [stepKey]: stepData,
      };
    }
    this.save(false);
    this.updateUrlStep(nextStepNumber);
  }

  onStep4Submit(stepData?: any): void {
    if (this.isSavingStep) {
      return;
    }

    this.setFormData(stepData, 'step4Data');
    if (this.formData) {
      this.formData.step = '5';
    }
    this.stepper.next(stepData);
  }

  save(saveAndExit: boolean) {
    const isClient = this.data?.user?.role === Roles.CLIENT;
    const completedStep = saveAndExit ? this.currentStep : this.currentStep - 1;
    const stepKey = `step${completedStep}Data` as 'step1Data' | 'step2Data' | 'step3Data' | 'step4Data';
    const stepData = this.formData[stepKey];

    const emitData = isClient
      ? { ...stepData, step: String(this.currentStep), saveAndExit }
      : { ...this.buildPayload(undefined, false, true), step: String(this.currentStep), saveAndExit };

    this.saveEvent.emit(emitData);
    if (saveAndExit) {
      this.dialogRef.close();
    }
  }

  close(): void {
    this.removeUrlStep();
    this.dialogRef.close();
  }

  private updateCurrentStep(step: number): void {
    if (this.currentStep !== step) {
      this.currentStep = step;
      this.updateStepperStep(step);
    }
  }

  private updateStepperStep(step: number): void {
    if (this.stepper && step >= 1 && step <= this.totalSteps) {
      this.stepper.currentStepIndex = step;
      this.stepper.navigation();
    }
  }

  private updateUrlStep(step: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { laft_step: step },
      queryParamsHandling: 'merge',
    });
  }

  private removeUrlStep(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { laft_step: null },
      replaceUrl: true,
    });
  }

  setFormData(data: any, step: string) {
    if (!data || typeof data !== 'object') {
      return;
    }

    this.formData = {
      ...this.formData,
      [step]: data,
    };

    if (step === 'step1Data' || step === 'step2Data') {
      this.updateLegalRepresentativesList();
      this.updateFirstLegalRepresentativeName();
    }
  }

  private updateLegalRepresentativesList(): void {
    const step1Data = this.formData?.step1Data || this.initFormData || {};
    const step2Data = this.formData?.step2Data || {};

    const list: { value: string; text: string }[] = [];
    const addedIds = new Set<string>();

    const addToList = (value: string, text: string, idNumber: string) => {
      const normalizedId = idNumber?.trim();
      if (text && normalizedId && !addedIds.has(normalizedId)) {
        addedIds.add(normalizedId);
        list.push({ value, text });
      }
    };

    const representativeNames = step1Data?.representativeNames ?? '';
    const representativeLastNames = step1Data?.representativeLastNames ?? '';
    const firstFullName = `${representativeNames} ${representativeLastNames}`.trim();
    const firstIdNumber = step1Data?.representativeIdNumber ?? '';
    addToList('first_representative', firstFullName, firstIdNumber);

    const secondRepresentativeNames = step1Data?.secondRepresentativeNames ?? '';
    const secondRepresentativeLastNames = step1Data?.secondRepresentativeLastNames ?? '';
    const secondFullName = `${secondRepresentativeNames} ${secondRepresentativeLastNames}`.trim();
    const secondIdNumber = step1Data?.secondRepresentativeNumber ?? '';
    addToList('second_representative', secondFullName, secondIdNumber);

    let shareHolders = step2Data?.shareHolders;
    if (!shareHolders || !Array.isArray(shareHolders) || shareHolders.length === 0) {
      shareHolders = this.initFormData?.shareHolders ?? [];
    }
    
    if (Array.isArray(shareHolders)) {
      shareHolders.forEach((shareholder: any, index: number) => {
        const shareholderName = shareholder?.fullName?.trim();
        const shareholderId = shareholder?.documentNumber?.trim() ?? '';
        addToList(`shareholder_${index}`, shareholderName, shareholderId);
      });
    }

    if (
      !this.isPendingPepFlow &&
      Array.isArray(this.initFormData?.airtableRepresentativeData)
    ) {
      this.initFormData.airtableRepresentativeData.forEach((rep: any, index: number) => {
        const repName = `${rep?.names || ''} ${rep?.lastNames || ''}`.trim();
        const repDocument = rep?.documentNumber || '';
        addToList(`airtable_rep_${repDocument || index}`, repName, repDocument);
      });
    }

    this.legalRepresentativesList = list;
  }

  private updateFirstLegalRepresentativeName(): void {
    const step1Data = this.formData?.step1Data || this.initFormData || {};
    const names = step1Data?.representativeNames ?? [];
    const lastNames = step1Data?.representativeLastNames ?? [];

    const firstName = Array.isArray(names) ? names[0] ?? '' : names ?? '';
    const firstLastName = Array.isArray(lastNames) ? lastNames[0] ?? '' : lastNames ?? '';

    if (!firstName && !firstLastName) {
      this.firstLegalRepresentativeName = '';
    } else {
      this.firstLegalRepresentativeName = `${firstName} ${firstLastName}`.trim();
    }
  }

  private checkPendingPepFlow(): void {
    this.isPendingPepFlow = !!(
      this.data?.initialStep === 3 &&
      this.initFormData?.pendingAdditionalPepInfo === true
    );

    if (this.isPendingPepFlow) {
      this.formData.step3Data = {};
      this.updateAirtableRepresentativesList();
      if (this.data?.user?.role === Roles.CLIENT) {
        this.mergeRootPepDataIntoAirtableRepsForClient();
      }
    }
  }

  private mergeRootPepDataIntoAirtableRepsForClient(): void {
    const rootPepList = Array.isArray(this.initFormData?.pepList) ? this.initFormData.pepList : [];
    const rootFamilyPepList = Array.isArray(this.initFormData?.familyPepList) ? this.initFormData.familyPepList : [];
    if (rootPepList.length === 0 && rootFamilyPepList.length === 0) return;

    const list = this.airtableRepresentativesList || [];
    const merged: any[] = list.map(rep => {
      const value = rep?.value ?? `airtable_rep_${(rep?.documentNumber || '').trim()}`;
      const doc = (rep?.documentNumber || '').trim();
      const base: any = {
        documentNumber: doc,
        names: rep?.names ?? '',
        lastNames: rep?.lastNames ?? '',
      };

      const pepEntries = rootPepList.filter((p: any) => (p?.peplegalRepresentative || '') === value);
      const familyEntries = rootFamilyPepList.filter((f: any) => (f?.pepRelativeslegalRepresentative || '') === value);

      const hasPep = pepEntries.length > 0;
      const hasRelatives = familyEntries.length > 0;
      base.isPEP = hasPep;
      base.hasPEPRelatives = hasRelatives;

      if (hasPep) {
        const first = pepEntries[0];
        base.hasBeenWorkingOnPublicInstitution = !!first?.hasBeenWorkingOnPublicInstitution;
        base.partnerName = first?.partnerName ?? '';
        base.positionPartner = first?.positionPartner ?? '';
        base.institutionName = first?.institutionName ?? '';
        base.family2ndDegreeMembers = Array.isArray(first?.family2ndDegreeMembers) ? first.family2ndDegreeMembers : [];
      }
      if (hasRelatives) {
        const first = familyEntries[0];
        base.familyMembers = Array.isArray(first?.familyMembers) ? first.familyMembers : [];
      }

      return base;
    });

    this.initFormData.airtableRepresentativeData = merged;
  }

  private updateAirtableRepresentativesList(): void {
    const missingRepresentatives = Array.isArray(this.initFormData?.missingRepresentatives)
      ? this.initFormData?.missingRepresentatives
      : [];

    const airtableData = Array.isArray(this.initFormData?.airtableRepresentativeData)
      ? this.initFormData?.airtableRepresentativeData
      : [];

    const normalizeDoc = (doc: string) => (doc || '').trim().replace(/\s/g, '');
    const step1Data = this.formData?.step1Data || this.initFormData || {};
    const existingRepDocNumbers = new Set<string>();

    const firstRepDoc = normalizeDoc(
      step1Data?.representativeIdNumber ??
        step1Data?.representative_id_number ??
        (step1Data?.representative as any)?.documentNumber ??
        ''
    );
    const secondRepDoc = normalizeDoc(
      step1Data?.secondRepresentativeNumber ??
        step1Data?.second_representative_number ??
        (step1Data?.secondRepresentative as any)?.documentNumber ??
        ''
    );

    if (firstRepDoc) existingRepDocNumbers.add(firstRepDoc);
    if (secondRepDoc) existingRepDocNumbers.add(secondRepDoc);

    let sourceList: any[] = [];

    if (missingRepresentatives.length > 0) {
      sourceList = missingRepresentatives.filter((rep: any) => {
        const documentNumber = normalizeDoc(rep?.documentNumber ?? '');
        if (!documentNumber) return false;
        if (existingRepDocNumbers.has(documentNumber)) return false;
        return true;
      });
    }

    if (sourceList.length === 0 && airtableData.length > 0) {
      sourceList = airtableData.filter((rep: any) => {
        const documentNumber = normalizeDoc(rep?.documentNumber ?? '');
        if (!documentNumber) return false;
        return !existingRepDocNumbers.has(documentNumber);
      });
    }

    const seenDocuments = new Set<string>();

    this.airtableRepresentativesList = sourceList
      .map((rep: any, index: number) => {
        const documentNumber = (rep.documentNumber || '').trim();
        if (!documentNumber || seenDocuments.has(documentNumber)) {
          return null;
        }
        seenDocuments.add(documentNumber);

        return {
          value: `airtable_rep_${documentNumber || index}`,
          text: `${rep.names || ''} ${rep.lastNames || ''}`.trim(),
          documentNumber,
          names: rep.names || '',
          lastNames: rep.lastNames || '',
        };
      })
      .filter(Boolean) as {
      value: string;
      text: string;
      documentNumber: string;
      names: string;
      lastNames: string;
    }[];
  }

  private buildPayload(
    formData?: any,
    clearPendingAdditionalPepInfo: boolean = false,
    isSaveFlow: boolean = false
  ): any {
    if (this.formData?.step3Data) {
      this.formData.step3Data = this.sanitizeStep3Data(this.formData.step3Data);
    }

    const init = this.initFormData || {};
    const payload = Object.assign(
      {},
      init,
      { step: this.formData.step },
      this.formData.step1Data,
      this.formData.step2Data,
      this.formData.step3Data,
      this.formData.step4Data,
      { ...(formData || {}) }
    );

    // Remove pepList and familyPepList from init data if they shouldn't be there
    // They will be added back by step3Data if needed
    const step3IsPEP = !!payload.isPEP;
    const step3HasPEPRelatives = !!payload.hasPEPRelatives;
    
    if (!step3IsPEP || !Array.isArray(payload.pepList) || payload.pepList.length === 0) {
      delete payload.pepList;
    }
    
    if (!step3HasPEPRelatives || !Array.isArray(payload.familyPepList) || payload.familyPepList.length === 0) {
      delete payload.familyPepList;
    }

    const transformedPayload = this.transformPendingPepData(payload, clearPendingAdditionalPepInfo);

    if (transformedPayload.isInvestigated) {
      if (transformedPayload.investigations === null || transformedPayload.investigations === undefined || !Array.isArray(transformedPayload.investigations)) {
        transformedPayload.investigations = [];
      }
    } else {
      if (transformedPayload.investigations === null || transformedPayload.investigations === undefined) {
        delete transformedPayload.investigations;
      }
    }

    // Always send as empty arrays never null (backend expects [] for these fields)
    transformedPayload.airtableRepresentativeData = Array.isArray(transformedPayload.airtableRepresentativeData)
      ? transformedPayload.airtableRepresentativeData
      : [];
    transformedPayload.missingRepresentatives = Array.isArray(transformedPayload.missingRepresentatives)
      ? transformedPayload.missingRepresentatives
      : [];

    Object.keys(transformedPayload).forEach(key => {
      if (transformedPayload[key] === null || transformedPayload[key] === undefined) {
        delete transformedPayload[key];
      }
    });

    // Remove pepList and familyPepList if they are empty arrays or if the corresponding flag is false
    const isPEP = !!transformedPayload.isPEP;
    const hasPEPRelatives = !!transformedPayload.hasPEPRelatives;

    if (!isPEP || !Array.isArray(transformedPayload.pepList) || transformedPayload.pepList.length === 0) {
      delete transformedPayload.pepList;
    }

    if (!hasPEPRelatives || !Array.isArray(transformedPayload.familyPepList) || transformedPayload.familyPepList.length === 0) {
      delete transformedPayload.familyPepList;
    }

    if (this.formData) {
      this.formData = {
        ...this.formData,
        step3Data: this.extractStep3DataFromPayload(transformedPayload),
      };
    }

    if (Array.isArray(transformedPayload.airtableRepresentativeData)) {
      transformedPayload.airtableRepresentativeData = this.sanitizeAirtableRepresentatives(
        transformedPayload.airtableRepresentativeData
      );
    } else if (
      !transformedPayload.airtableRepresentativeData &&
      Array.isArray(this.initFormData?.airtableRepresentativeData)
    ) {
      transformedPayload.airtableRepresentativeData = this.sanitizeAirtableRepresentatives(
        this.initFormData.airtableRepresentativeData
      );
    }

    if (
      typeof transformedPayload.pendingAdditionalPepInfo === 'undefined' &&
      typeof this.initFormData?.pendingAdditionalPepInfo !== 'undefined' &&
      this.initFormData.pendingAdditionalPepInfo !== null
    ) {
      transformedPayload.pendingAdditionalPepInfo = this.initFormData.pendingAdditionalPepInfo;
    }

    if (transformedPayload.pendingAdditionalPepInfo === null) {
      delete transformedPayload.pendingAdditionalPepInfo;
    }

    return transformedPayload;
  }

  private sanitizeStep3Data(step3Data: any) {
    if (!step3Data || typeof step3Data !== 'object') {
      return step3Data;
    }

    const isPEP = !!step3Data.isPEP;
    const hasPEPRelatives = !!step3Data.hasPEPRelatives;
    const sanitized: any = {
      isPEP,
      hasPEPRelatives,
      ...(step3Data.isPendingPepFlow ? { isPendingPepFlow: step3Data.isPendingPepFlow } : {}),
      ...(step3Data.pendingPepFlowMetadata ? { pendingPepFlowMetadata: step3Data.pendingPepFlowMetadata } : {}),
    };

    // Only include pepList if isPEP is true and has items
    if (isPEP && Array.isArray(step3Data.pepList) && step3Data.pepList.length > 0) {
      sanitized.pepList = step3Data.pepList;
    }

    // Only include familyPepList if hasPEPRelatives is true and has items
    if (hasPEPRelatives && Array.isArray(step3Data.familyPepList) && step3Data.familyPepList.length > 0) {
      sanitized.familyPepList = step3Data.familyPepList;
    }

    return sanitized;
  }

  private extractStep3DataFromPayload(payload: any) {
    const isPEP = !!payload?.isPEP;
    const hasPEPRelatives = !!payload?.hasPEPRelatives;
    const extracted: any = {
      isPEP,
      hasPEPRelatives,
    };

    // Only include pepList if isPEP is true and has items
    if (isPEP && Array.isArray(payload?.pepList) && payload.pepList.length > 0) {
      extracted.pepList = payload.pepList;
    }

    // Only include familyPepList if hasPEPRelatives is true and has items
    if (hasPEPRelatives && Array.isArray(payload?.familyPepList) && payload.familyPepList.length > 0) {
      extracted.familyPepList = payload.familyPepList;
    }

    return extracted;
  }

  private transformPendingPepData(payload: any, clearPending: boolean) {
    if (!payload?.pendingPepFlowMetadata || !Array.isArray(payload.pendingPepFlowMetadata?.representatives)) {
      if (payload) {
        delete payload?.isPendingPepFlow;
        if (clearPending) {
          payload.pendingAdditionalPepInfo = false;
        }
        payload.missingRepresentatives = Array.isArray(this.initFormData?.missingRepresentatives)
          ? this.initFormData.missingRepresentatives
          : [];
      }
      return payload;
    }

    const metadata = payload.pendingPepFlowMetadata;

    const existingRepresentatives = Array.isArray(this.initFormData?.airtableRepresentativeData)
      ? this.initFormData.airtableRepresentativeData
          .filter(rep => !!rep)
          .map(rep => ({ ...rep }))
      : [];

    const representativesMap = new Map<string, any>();
    existingRepresentatives.forEach(rep => {
      const documentNumber = (rep?.documentNumber || '').trim();
      if (documentNumber) {
        representativesMap.set(documentNumber, { ...rep });
      }
    });

    metadata.representatives.forEach(repMeta => {
      const documentNumber = (repMeta?.documentNumber || '').trim();
      if (!documentNumber) {
        return;
      }

      const existingRep = representativesMap.get(documentNumber) || {
        documentNumber,
        names: repMeta?.names || '',
        lastNames: repMeta?.lastNames || '',
      };

      const pepInfo = repMeta?.pepData;
      const relativesInfo = repMeta?.relativesData;
      const globalIsPep = !!metadata?.globalAnswers?.isPEP;
      const globalHasRelatives = !!metadata?.globalAnswers?.hasPEPRelatives;

      existingRep.isPEP = globalIsPep && !!pepInfo;
      existingRep.hasBeenWorkingOnPublicInstitution =
        existingRep.isPEP && !!pepInfo?.hasBeenWorkingOnPublicInstitution;

      if (existingRep.hasBeenWorkingOnPublicInstitution) {
        const pos = (pepInfo?.positionPartner || '').trim();
        const inst = (pepInfo?.institutionName || '').trim();
        if (pos) existingRep.positionPartner = pos;
        if (inst) existingRep.institutionName = inst;
      } else {
        delete existingRep.positionPartner;
        delete existingRep.institutionName;
      }

      const partnerNameVal = (existingRep.isPEP ? pepInfo?.partnerName : '') || '';
      if (partnerNameVal.trim()) existingRep.partnerName = partnerNameVal.trim();
      else delete existingRep.partnerName;
      existingRep.family2ndDegreeMembers =
        existingRep.isPEP && Array.isArray(pepInfo?.family2ndDegreeMembers)
          ? pepInfo.family2ndDegreeMembers
          : [];

      existingRep.hasPEPRelatives = globalHasRelatives && !!relativesInfo;
      existingRep.familyMembers =
        existingRep.hasPEPRelatives && Array.isArray(relativesInfo?.familyMembers)
          ? relativesInfo.familyMembers
          : [];

      representativesMap.set(documentNumber, existingRep);
    });

    const updatedRepresentatives = Array.from(representativesMap.values());

    payload.airtableRepresentativeData = this.sanitizeAirtableRepresentatives(updatedRepresentatives);
    payload.pendingAdditionalPepInfo = clearPending
      ? false
      : payload.pendingAdditionalPepInfo ?? this.initFormData?.pendingAdditionalPepInfo;
    payload.missingRepresentatives = Array.isArray(this.initFormData?.missingRepresentatives)
      ? this.initFormData.missingRepresentatives
      : [];

    delete payload.pendingPepFlowMetadata;
    delete payload.isPendingPepFlow;

    // Only include pepList and familyPepList in initFormData if they have items
    const updatedInitFormData: any = {
      ...this.initFormData,
      airtableRepresentativeData: updatedRepresentatives,
      pendingAdditionalPepInfo: clearPending
        ? false
        : this.initFormData?.pendingAdditionalPepInfo ?? payload?.pendingAdditionalPepInfo ?? true,
      isPEP: payload?.isPEP ?? this.initFormData?.isPEP,
      hasPEPRelatives: payload?.hasPEPRelatives ?? this.initFormData?.hasPEPRelatives,
      missingRepresentatives: Array.isArray(this.initFormData?.missingRepresentatives)
        ? this.initFormData.missingRepresentatives
        : [],
    };

    const payloadIsPEP = !!updatedInitFormData.isPEP;
    const payloadHasPEPRelatives = !!updatedInitFormData.hasPEPRelatives;

    if (payloadIsPEP && Array.isArray(payload?.pepList) && payload.pepList.length > 0) {
      updatedInitFormData.pepList = payload.pepList;
    } else if (payloadIsPEP && Array.isArray(this.initFormData?.pepList) && this.initFormData.pepList.length > 0) {
      updatedInitFormData.pepList = this.initFormData.pepList;
    }

    if (payloadHasPEPRelatives && Array.isArray(payload?.familyPepList) && payload.familyPepList.length > 0) {
      updatedInitFormData.familyPepList = payload.familyPepList;
    } else if (payloadHasPEPRelatives && Array.isArray(this.initFormData?.familyPepList) && this.initFormData.familyPepList.length > 0) {
      updatedInitFormData.familyPepList = this.initFormData.familyPepList;
    }

    this.initFormData = updatedInitFormData;

    if (clearPending) {
      this.isPendingPepFlow = false;
    }

    return payload;
  }

  /**
   * Removes empty strings from airtableRepresentativeData items to avoid BE validation errors.
   */
  private sanitizeAirtableRepresentatives(reps: any[]): any[] {
    if (!Array.isArray(reps)) return reps;

    return reps.map(rep => {
      const sanitized = { ...rep };

      // Remove empty/null optional string fields
      [
        'positionPartner',
        'institutionName',
        'partnerName',
        'certificateCopyDate',
        'escrituraPublicaDate',
        'actaDate',
        'notarioNames',
        'otherInstrument',
      ].forEach(key => {
        if (sanitized[key] === '' || sanitized[key] == null || sanitized[key] === undefined) {
          delete sanitized[key];
        }
      });

      if (Array.isArray(sanitized.family2ndDegreeMembers)) {
        sanitized.family2ndDegreeMembers = sanitized.family2ndDegreeMembers.filter(
          (m: any) => m?.fullName?.trim() && m?.relationship?.trim()
        );
      }
      if (Array.isArray(sanitized.familyMembers)) {
        sanitized.familyMembers = sanitized.familyMembers.filter(
          (m: any) => m?.fullName?.trim() && m?.relationship?.trim()
        );
      }

      return sanitized;
    });
  }
}
